import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { janeApi, safeParseAsync } from '@jane/shared/data-access';
import type { Kiosk } from '@jane/shared/types';

import { useGetJwt, useSaveJwt } from './jwt';
import { zKiosk } from './kiosk.types';

type GetWhoAmiReturnType = {
  kiosk: Kiosk | null;
};

const getWhoami = async (
  jwt?: string | null,
  cartUuid?: string
): Promise<Kiosk | null> => {
  const url = `/kiosk/whoami${cartUuid ? `?cart_uuid=${cartUuid}` : ''}`;

  const response = await janeApi.get<GetWhoAmiReturnType>(url, jwt);

  return await safeParseAsync(zKiosk, response.kiosk, 'getWhoami');
};

export const getWhoamiQueryOptions = (
  jwt: string | null | undefined,
  cartUuid: string | undefined
) =>
  queryOptions({
    queryFn: () => getWhoami(jwt, cartUuid),
    queryKey: ['whoami', jwt, cartUuid],
  });

interface UseGetWhoamiArgs {
  cartUuid?: string;
  initialData: Kiosk | null;
}

export function useGetWhoami({ cartUuid, initialData }: UseGetWhoamiArgs) {
  const location = useLocation();
  const mobilePath = location.pathname.includes('/mobile');
  const { data: jwt, isSuccess } = useGetJwt();
  const { mutate: saveJwt } = useSaveJwt();
  const kioskDeviceExpirationRedirectEnabled = useFlag(
    FLAGS.c3KioskDeviceExpirationRedirectEnabled
  );

  const query = useQuery({
    ...getWhoamiQueryOptions(jwt, cartUuid),
    enabled: isSuccess && !mobilePath,
    initialData,
    staleTime: kioskDeviceExpirationRedirectEnabled
      ? 5 * 60 * 1000 // 5 minutes
      : 24 * 60 * 60 * 1000, // 24 hours
  });

  useEffect(() => {
    // Clear (expired) jwt if it results in an empty whoami
    if (jwt && query.isSuccess && !query.data?.id && !mobilePath) {
      saveJwt(null);
    }
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
  }, [jwt, query, saveJwt, mobilePath]);

  return query;
}
